// Plays a Spanish item: the coach's own recording when one was uploaded,
// otherwise the device's built-in Spanish voice (speech synthesis).
// When neither can produce sound, a short message says why instead of staying silent.
import api from '../api';

let current = null;
let toastTimer = null;

// The standard voice is served by our own backend as a relative path (/api/spanish/audio/...)
function resolveAudioUrl(url) {
  if (!url || /^https?:/i.test(url)) return url;
  try { return new URL(api.defaults.baseURL).origin + url; } catch (e) { return url; }
}

function toast(message) {
  try {
    let el = document.getElementById('sp-audio-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sp-audio-toast';
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;left:12px;right:12px;bottom:84px;z-index:100000;max-width:460px;margin:0 auto;' +
        'background:#2c3e50;color:#fff;padding:12px 14px;border-radius:12px;font-size:13px;line-height:1.4;' +
        'box-shadow:0 6px 20px rgba(0,0,0,0.3);text-align:center;';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.display = 'block';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.style.display = 'none'; }, 6000);
  } catch (e) { /* ignore */ }
}

export function stopSpanishAudio() {
  try { if (current) { current.pause(); current = null; } } catch (e) { /* ignore */ }
  try { if (window.speechSynthesis && (window.speechSynthesis.speaking || window.speechSynthesis.pending)) window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
}

// Browsers load their voice list asynchronously - wait for it (briefly) instead of reading an empty list.
function loadVoices() {
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const now = synth.getVoices();
    if (now && now.length) return resolve(now);
    let done = false;
    const finish = () => { if (done) return; done = true; synth.removeEventListener && synth.removeEventListener('voiceschanged', finish); resolve(synth.getVoices() || []); };
    synth.addEventListener && synth.addEventListener('voiceschanged', finish);
    setTimeout(finish, 1200);
  });
}

/** Resolves 'ok' | 'unsupported' | 'no-voice' | 'error' */
export async function speakSpanish(text) {
  const synth = window.speechSynthesis;
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') return 'unsupported';

  const voices = await loadVoices();
  const voice = voices.find(v => /^es[-_]ES/i.test(v.lang)) || voices.find(v => /^es([-_]|$)/i.test(v.lang));
  if (voices.length > 0 && !voice) return 'no-voice';

  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(text.replace(/[¡¿]/g, ''));
    u.lang = voice ? voice.lang : 'es-ES';
    if (voice) u.voice = voice;
    u.rate = 0.9;
    u.volume = 1;
    let started = false;
    u.onstart = () => { started = true; };
    u.onend = () => resolve('ok');
    u.onerror = (e) => resolve(e && (e.error === 'canceled' || e.error === 'interrupted') ? 'ok' : 'error');
    try {
      if (synth.paused) synth.resume();
      synth.speak(u);
    } catch (e) { resolve('error'); return; }
    // Some WebViews accept speak() but never start - don't stay silent about it
    setTimeout(() => { if (!started && !synth.speaking) resolve('error'); }, 2500);
  });
}

const PROBLEM = {
  unsupported: 'This device can\'t read words aloud. Ask your coach to record the audio, or open CoachPad in Chrome/Safari.',
  'no-voice': 'No Spanish voice is installed on this device. Add one in your phone\'s text-to-speech settings, or ask your coach to record the audio.',
  error: 'Couldn\'t play the sound. Check that your phone isn\'t on silent and the volume is up, then try again.'
};

export async function playSpanish(item, text) {
  stopSpanishAudio();
  const phrase = text || item.es;

  if (item.audioUrl) {
    try {
      const audio = new Audio(resolveAudioUrl(item.audioUrl));
      current = audio;
      await audio.play();
      return;
    } catch (e) {
      // blocked / unreachable file -> fall back to the built-in voice below
    }
  }

  const result = await speakSpanish(phrase);
  if (result !== 'ok') toast(PROBLEM[result] || PROBLEM.error);
}
